import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeaturesContext = createContext(null);

/**
 * useFeatures — full features payload from /api/me/features
 * Returns: { planId, displayName, tierLabel, features, upgradeTo, loading, refresh }
 */
export const useFeatures = () => {
  const context = useContext(FeaturesContext);
  if (!context) {
    throw new Error('useFeatures must be used within FeaturesProvider');
  }
  return context;
};

/**
 * useMode — convenience hook describing the user's selected onboarding mode.
 *
 * Returns: {
 *   mode,          // raw mode id ('health_individual', 'performance_individual', ...)
 *   isHealth,      // true for health_individual or health_family
 *   isPerformance, // true for performance_individual or performance_team
 *   isFamily,      // true for health_family
 *   isTeam,        // true for performance_team
 *   isCoach,       // true for coach mode
 *   variant,       // simplified UI variant: 'health' | 'performance' | 'family' | 'team' | 'coach' | null
 *   loading,
 * }
 */
export const useMode = () => {
  const { selectedMode, loading } = useFeatures();
  const variant = (() => {
    if (!selectedMode) return null;
    if (selectedMode === 'health_family') return 'family';
    if (selectedMode === 'performance_team') return 'team';
    if (selectedMode === 'performance_individual') return 'performance';
    if (selectedMode === 'coach') return 'coach';
    return 'health';
  })();
  return {
    mode: selectedMode,
    isHealth: selectedMode === 'health_individual' || selectedMode === 'health_family',
    isPerformance: selectedMode === 'performance_individual' || selectedMode === 'performance_team',
    isFamily: selectedMode === 'health_family',
    isTeam: selectedMode === 'performance_team',
    isCoach: selectedMode === 'coach',
    variant,
    loading,
  };
};

/**
 * useFeature(featureName) — single feature accessor.
 *  - Boolean flag → true/false
 *  - List flag    → true if non-empty
 *  - Numeric      → true if != 0 (treats -1 as unlimited = true)
 * Returns: { enabled, value, upgradeTo, loading }
 */
export const useFeature = (featureName) => {
  const { features, upgradeTo, loading } = useFeatures();
  const value = features?.[featureName];

  let enabled = false;
  if (value === undefined || value === null) enabled = false;
  else if (typeof value === 'boolean') enabled = value;
  else if (Array.isArray(value)) enabled = value.length > 0;
  else if (typeof value === 'number') enabled = value !== 0;
  else if (typeof value === 'string') enabled = !['', 'none', 'blocked'].includes(value);
  else enabled = Boolean(value);

  return { enabled, value, upgradeTo, loading };
};

export const FeaturesProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [data, setData] = useState({
    planId: null,
    displayName: null,
    tierLabel: null,
    features: null,
    upgradeTo: null,
    availableModes: [],
    selectedMode: null,
  });
  const [loading, setLoading] = useState(true);

  const fetchFeatures = useCallback(async () => {
    if (!token || !user) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/me/features`);
      setData({
        planId: res.data.plan_id,
        displayName: res.data.display_name,
        tierLabel: res.data.tier_label,
        features: res.data.features,
        upgradeTo: res.data.upgrade_to,
        availableModes: res.data.available_modes || [],
        selectedMode: res.data.selected_mode,
      });
    } catch (err) {
      console.error('Error loading features:', err);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  return (
    <FeaturesContext.Provider
      value={{
        ...data,
        loading,
        refresh: fetchFeatures,
      }}
    >
      {children}
    </FeaturesContext.Provider>
  );
};
